import { CompactUserResponse, UserId } from '@business/entities/account';
import { PageQuery, Paginate } from '@business/entities/common';
import { CompactMarketingBoardResponse, MarketingBoardId, MarketingBoardResponse, SubscriptionBoardId } from '@business/entities/marketing';
import { Endpoint } from '../endpoint';
import {
  MBNoteId,
  MBNoteResponse,
  MBServiceId,
  MBServiceResponse,
  MBServiceStepResponse,
  MBSubscriptionId,
  MBSubscriptionResponse,
  MBServiceSectionResponse,
} from './../../entities/marketing/MarketingBoardResponse';

export function fetchMarketingBoards(params: PageQuery) {
  return Endpoint.get<Paginate<CompactMarketingBoardResponse>>('@gc/marketing-board', { params });
}

export function fetchMarketingBoard(id: MarketingBoardId, subBoardId: SubscriptionBoardId) {
  return Endpoint.get<MarketingBoardResponse>(`@gc/marketing-board/board/${id}`, { params: { subBoardId } });
}

export function fetchQuickMarketingBoard(id: MarketingBoardId, subBoardId: SubscriptionBoardId) {
  return Endpoint.get<MarketingBoardResponse>(`@gc/marketing-board/quick-board/${id}`, { params: { subBoardId } });
}

export function postSetting(subId: MBSubscriptionId, description: string) {
  return Endpoint.post<MBSubscriptionResponse>(`@gc/marketing-board/setting/${subId}`, { data: { description } });
}

export function toggleStep(id: string, feedback?: string, notes?: string) {
  return Endpoint.post<MBServiceStepResponse>(`@gc/marketing-board/toggle-step/${id}`, { data: { feedback, notes } });
}

export function createNote(boardId: MarketingBoardId, data: Partial<MBNoteResponse>) {
  return Endpoint.post<MBNoteResponse>(`@gc/marketing-board/note/${boardId}`, { data });
}

export function updateNote(noteId: MBNoteId, data: Partial<MBNoteResponse>) {
  return Endpoint.patch<MBNoteResponse>(`@gc/marketing-board/note/${noteId}`, { data });
}

export function deleteNote(noteId: MBNoteId) {
  return Endpoint.delete<void>(`@gc/marketing-board/note/${noteId}`);
}

export function toggleOverride(subId: MBSubscriptionId, serviceId: MBServiceId, enable: boolean) {
  return Endpoint.post<MBServiceResponse>(`@gc/marketing-board/toggle-override/${subId}/${serviceId}`, { data: { enable } });
}

export function boardCall(boardId: MarketingBoardId, action: 'scheduleCall' | 'completedCall', forDate: number) {
  return Endpoint.post<MBServiceResponse>(`@gc/marketing-board/note-actions/${boardId}`, { data: { forDate, action } });
}

export function updateManager(boardId: MarketingBoardId, manager: UserId) {
  return Endpoint.patch<CompactUserResponse>(`@gc/marketing-board/update-manager/${boardId}`, { data: { manager } });
}

export function resetChecklist(subId: MBSubscriptionId) {
  return Endpoint.post<MarketingBoardResponse>(`@gc/marketing-board/reset-checklists/${subId}`);
}

export function fetchMarketingBoardUser() {
  return Endpoint.get<CompactUserResponse[]>('@gc/marketing-board/account-manager');
}

/** Start Section **/
export function createSection(subId: MBSubscriptionId, serviceId: MBServiceId, name: string) {
  return Endpoint.post<MBServiceSectionResponse[]>(`@gc/marketing-board/create-section`, { data: { subId, serviceId, name } });
}

export function updateSection(boardId: MarketingBoardId, subId: MBSubscriptionId, serviceId: MBServiceId, sectionId: string, name: string) {
  return Endpoint.patch<MBServiceSectionResponse[]>(`@gc/marketing-board/update-section/${sectionId}`, { data: { name, subId, serviceId } });
}

export function deleteSection(id: string, subId: MBSubscriptionId, serviceId: MBServiceId) {
  return Endpoint.delete<MBServiceSectionResponse[]>(`@gc/marketing-board/section/${id}`, { data: { subId, serviceId } });
}

export function sortSections(subId: MBSubscriptionId, serviceId: MBServiceId, ids: string[]) {
  return Endpoint.post<MBServiceSectionResponse[]>(`@gc/marketing-board/sort-section`, { data: { ids, subId, serviceId } });
}

/** End Section **/

/** Start Step **/
export function createStep(subId: MBSubscriptionId, serviceId: MBServiceId, id: number, data: Partial<MBServiceStepResponse>) {
  return Endpoint.post<MBServiceSectionResponse[]>(`@gc/marketing-board/create-step/${id}`, {
    data: { serviceId, subId, data },
  });
}

export function updateSetupStep(subId: MBSubscriptionId, serviceId: MBServiceId, id: string, data: Partial<MBServiceStepResponse>) {
  return Endpoint.patch<MBServiceSectionResponse[]>(`@gc/marketing-board/update-step/${id}`, { data: { subId, serviceId, data } });
}

export function deleteSetupStep(id: string, subId: MBSubscriptionId, serviceId: MBServiceId) {
  return Endpoint.delete<MBServiceSectionResponse[]>(`@gc/marketing-board/setup-step/${id}`, { data: { subId, serviceId } });
}

export function sortSectionSteps(subId: MBSubscriptionId, serviceId: MBServiceId, ids: string[]) {
  return Endpoint.post<MBServiceSectionResponse[]>(`@gc/marketing-board/sort-step`, { data: { subId, serviceId, ids } });
}

/** End Step **/
